import {
  EMPLOYEE_CATEGORY_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
} from "model/OrganizationalStructure";
import { PaymentMethod } from "model/Gig";
import { SalaryType } from "model/Salary";
import type {
  EmploymentOptions,
  SelectOption,
} from "../../profilePanel.contract";
import type { EmploymentUiRules } from "../../../shared/staffEmploymentRules.logic";
import {
  STAFF_TAX_TABLE_OPTIONS,
  toEmploymentTypeOptions,
  toTranslatedOptions,
} from "../../../shared/staffPanel.options.logic";
import { toVacationPayoutOptions } from "../../../shared/staffVacation.logic";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Builds translated employment-related dropdown options and applies business rules.
 */
export const toEmploymentOptions = (
  t: TranslateFn,
  rules: EmploymentUiRules,
): EmploymentOptions => {
  const employmentOptions: SelectOption[] = toEmploymentTypeOptions({
    canSelectSimflexityEmployee: rules.canSelectSimflexityEmployee,
    translate: t,
  });

  const salaryOptionsAll: SelectOption[] = toTranslatedOptions(
    SALARY_OPTIONS,
    t,
  );

  // Simflexity and hourly temporary workers can only use hourly salary.
  const isHourlySalaryOnly =
    rules.isSimflexityEmployee || rules.isHourlyTemporaryEmployee;
  const salaryOptions = isHourlySalaryOnly
    ? salaryOptionsAll.filter((option) => option.value !== SalaryType.Monthly)
    : salaryOptionsAll;

  const paymentOptionsAll: SelectOption[] = toTranslatedOptions(
    PAYMENT_TYPE_OPTIONS,
    t,
  );

  // Simflexity employees are forced to SimflexityPay, so only offer that.
  const paymentOptions = rules.isSimflexityEmployee
    ? paymentOptionsAll.filter(
        (option) => option.value === PaymentMethod.SIMFLEXITY_PAY,
      )
    : paymentOptionsAll.filter((option) => {
        if (option.value === PaymentMethod.SIMFLEXITY_PAY) {
          return rules.canSelectSimflexityPay;
        }
        return true;
      });

  const employeeCategoryOptions: SelectOption[] = toTranslatedOptions(
    EMPLOYEE_CATEGORY_OPTIONS,
    t,
  );
  const employeeCategoryLabelByValue = employeeCategoryOptions.reduce<
    Record<string, string>
  >((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  }, {});

  return {
    taxTableOptions: STAFF_TAX_TABLE_OPTIONS,
    employmentOptions,
    salaryOptions,
    paymentOptions,
    employeeCategoryOptions,
    employeeCategoryLabelByValue,
    vacationPayoutOptions: toVacationPayoutOptions(t),
  };
};
